<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

// Current account
const currentAccount = computed(() => store.getters.getCurrentAccount);

// Steps
const currentStep = ref(1);
const totalSteps = 4;

// Selected LIA version
const selectedVersion = ref(null);

// Prompt AI Assistant
const showPromptAssistant = ref(false);
const isGeneratingPrompt = ref(false);
const promptAssistantInput = ref({
  businessType: '',
  agentRole: '',
  tone: 'professional',
  features: [],
});

// Templates
const templates = ref([]);
const selectedTemplate = ref(null);
const isLoadingTemplates = ref(true);

// Filter templates by selected version
const filteredTemplates = computed(() => {
  if (!selectedVersion.value) return [];
  return templates.value.filter(t => {
    // Use direct version field from API response (falls back to metadata for backwards compatibility)
    const templateVersion = t.version || t.metadata?.version || '1.4';
    return templateVersion === selectedVersion.value.version;
  });
});

// Version info for display
const versionInfo = {
  '1.0': {
    name: 'LIA 1.0',
    description: 'Atendimento básico e automatizado',
    icon: 'i-lucide-bot',
    color: 'green',
  },
  1.4: {
    name: 'LIA 1.4',
    description: 'Atendimento inteligente e automatizado',
    icon: 'i-lucide-message-circle',
    color: 'blue',
  },
  2.1: {
    name: 'LIA 2.1',
    description: 'Agendamento inteligente e integrações',
    icon: 'i-lucide-calendar',
    color: 'purple',
  },
  3.1: {
    name: 'LIA 3.1',
    description: 'Integrações avançadas e automação completa',
    icon: 'i-lucide-database',
    color: 'pink',
  },
};

// Entitlements
const entitlements = computed(() => store.getters['liaAgents/getEntitlements']);
const availableVersions = computed(
  () => store.getters['liaAgents/getAvailableVersions']
);

// Agent Config - company_name pre-filled from account
const agentConfig = ref({
  name: '',
  company_name: '',
  system_prompt: '',
  model: 'gpt-4.1-mini',
  language: 'pt-BR',
  inbox_ids: [],
});

// Inboxes
const inboxes = computed(() => store.getters['inboxes/getInboxes']);

// Is Saving
const isSaving = ref(false);

// Fetch templates and entitlements
const fetchData = async () => {
  isLoadingTemplates.value = true;
  try {
    await Promise.all([
      store.dispatch('liaAgents/fetchTemplates'),
      store.dispatch('liaAgents/fetchEntitlements'),
    ]);
    templates.value = store.getters['liaAgents/getTemplates'];
  } catch (error) {
    useAlert(t('LIA_AGENTS.CREATE.FETCH_TEMPLATES_ERROR'));
  } finally {
    isLoadingTemplates.value = false;
  }
};

onMounted(() => {
  fetchData();
  store.dispatch('inboxes/get');
});

// Watch for account changes to auto-fill company name
watch(
  currentAccount,
  newAccount => {
    if (newAccount?.name && !agentConfig.value.company_name) {
      agentConfig.value.company_name = newAccount.name;
    }
  },
  { immediate: true }
);

// Prompt AI Assistant
const toneOptions = [
  {
    id: 'professional',
    name: 'Profissional',
    desc: 'Tom formal e corporativo',
  },
  { id: 'friendly', name: 'Amigável', desc: 'Tom casual e acolhedor' },
  { id: 'technical', name: 'Técnico', desc: 'Tom especializado e detalhado' },
  { id: 'sales', name: 'Vendedor', desc: 'Tom persuasivo e convincente' },
];

const featureOptions = [
  { id: 'multilingual', label: 'Suporte multilíngue' },
  { id: 'empathy', label: 'Respostas empáticas' },
  { id: 'upsell', label: 'Sugestões de upsell' },
  { id: 'faq', label: 'Respostas a FAQs' },
  { id: 'scheduling', label: 'Agendamento' },
  { id: 'handoff', label: 'Transferência para humano' },
];

const toggleFeature = featureId => {
  const index = promptAssistantInput.value.features.indexOf(featureId);
  if (index === -1) {
    promptAssistantInput.value.features.push(featureId);
  } else {
    promptAssistantInput.value.features.splice(index, 1);
  }
};

const generatePromptWithAI = async () => {
  isGeneratingPrompt.value = true;

  const { businessType, agentRole, tone, features } =
    promptAssistantInput.value;
  const companyName =
    agentConfig.value.company_name || currentAccount.value?.name || 'a empresa';

  const toneDescriptions = {
    professional: 'profissional, formal e corporativo',
    friendly: 'amigável, casual e acolhedor',
    technical: 'técnico, especializado e detalhado',
    sales: 'persuasivo, convincente e orientado a vendas',
  };

  const generatedPrompt = `## IDENTIDADE
Você é ${agentConfig.value.name || 'um assistente virtual'}, ${agentRole || 'atendente'} da ${companyName}${businessType ? `, uma empresa de ${businessType}` : ''}.

### PERSONALIDADE
- **Tom:** ${toneDescriptions[tone] || 'profissional'}
- **Objetivo:** Resolver as dúvidas e necessidades dos clientes de forma eficiente
- **Estilo:** Direto, claro e objetivo

## CONTEXTO
- A conversa se passa no WhatsApp
- O nome do cliente está disponível no perfil
- Sempre se apresente no início da conversa

## TAREFA
Sua principal tarefa é ajudar os clientes com suas dúvidas e necessidades relacionadas a ${companyName}.

${features.includes('handoff') ? '- Se não conseguir resolver o problema, transfira para um atendente humano\n' : ''}${features.includes('scheduling') ? '- Você pode ajudar a agendar horários e compromissos\n' : ''}${features.includes('upsell') ? '- Quando apropriado, sugira produtos ou serviços adicionais\n' : ''}${features.includes('faq') ? '- Responda perguntas frequentes de forma clara e concisa\n' : ''}${features.includes('empathy') ? '- Demonstre empatia e compreensão com as situações dos clientes\n' : ''}${features.includes('multilingual') ? '- Responda no idioma em que o cliente escrever\n' : ''}

## RESTRIÇÕES
- JAMAIS invente informações que não conhece
- Não compartilhe informações confidenciais
- Mantenha sempre um tom ${toneDescriptions[tone]?.split(',')[0] || 'profissional'}
- Se não souber algo, admita e ofereça transferir para um especialista

## FORMATO DE RESPOSTA
- Use mensagens curtas e objetivas (máximo 3 parágrafos)
- Use emojis com moderação quando apropriado
- Quebre mensagens longas em partes menores`;

  await new Promise(resolve => setTimeout(resolve, 1500));

  agentConfig.value.system_prompt = generatedPrompt;
  showPromptAssistant.value = false;
  isGeneratingPrompt.value = false;
  useAlert(
    'Prompt gerado com sucesso! Revise e personalize conforme necessário.'
  );
};

// Template icons
const templateIcons = {
  atendimento: 'i-lucide-message-circle',
  vendas: 'i-lucide-shopping-cart',
  agendamento: 'i-lucide-calendar',
  suporte: 'i-lucide-headphones',
  default: 'i-lucide-bot',
};

const getTemplateIcon = template => {
  const category = template.category?.toLowerCase() || 'default';
  return templateIcons[category] || templateIcons.default;
};

// Select LIA version
const selectVersion = version => {
  selectedVersion.value = version;
  selectedTemplate.value = null;
  currentStep.value = 2;
};

// Select template
const selectTemplate = template => {
  selectedTemplate.value = template;
  // Pre-fill from template
  if (template.default_system_prompt) {
    agentConfig.value.system_prompt = template.default_system_prompt;
  }
  if (template.default_model) {
    agentConfig.value.model = template.default_model;
  }
  currentStep.value = 3;
};

// Navigation
const goBack = () => {
  if (currentStep.value > 1) {
    // If going back from template selection, clear template
    if (currentStep.value === 2) {
      selectedVersion.value = null;
    }
    // If going back from config, clear template
    if (currentStep.value === 3) {
      selectedTemplate.value = null;
    }
    currentStep.value -= 1;
  } else {
    router.push(accountScopedRoute('lia_agents_list'));
  }
};

const goNext = () => {
  if (currentStep.value < totalSteps) {
    currentStep.value += 1;
  }
};

// Validation
const isStep3Valid = computed(() => {
  return (
    agentConfig.value.name.trim() !== '' &&
    agentConfig.value.system_prompt.trim() !== ''
  );
});

const isStep4Valid = computed(() => {
  return agentConfig.value.inbox_ids.length > 0;
});

// Toggle inbox
const toggleInbox = inboxId => {
  const index = agentConfig.value.inbox_ids.indexOf(inboxId);
  if (index === -1) {
    agentConfig.value.inbox_ids.push(inboxId);
  } else {
    agentConfig.value.inbox_ids.splice(index, 1);
  }
};

// Create agent
const createAgent = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('liaAgents/create', {
      lia_agent_template_id: selectedTemplate.value?.id,
      version:
        selectedVersion.value?.version ||
        selectedTemplate.value?.metadata?.version ||
        '1.4',
      ...agentConfig.value,
    });
    useAlert(t('LIA_AGENTS.CREATE.SUCCESS'));
    router.push(accountScopedRoute('lia_agents_list'));
  } catch (error) {
    useAlert(error.message || t('LIA_AGENTS.CREATE.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

// Languages available
const languages = [
  { id: 'pt-BR', name: 'Português (Brasil)' },
  { id: 'es', name: 'Español' },
  { id: 'en', name: 'English' },
];
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <header class="px-6 py-4 border-b border-n-weak bg-n-solid-2">
      <div class="flex items-center gap-4">
        <button
          class="p-2 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-5" />
        </button>
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('LIA_AGENTS.CREATE.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11 mt-0.5">
            {{
              t('LIA_AGENTS.CREATE.STEP', {
                current: currentStep,
                total: totalSteps,
              })
            }}
          </p>
        </div>
      </div>

      <!-- Progress bar -->
      <div class="flex gap-2 mt-4">
        <div
          v-for="step in totalSteps"
          :key="step"
          class="flex-1 h-1 rounded-full transition-colors"
          :class="step <= currentStep ? 'bg-woot-500' : 'bg-n-alpha-2'"
        />
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 overflow-auto">
      <!-- Step 1: Choose LIA Version -->
      <div v-if="currentStep === 1" class="p-6 max-w-3xl mx-auto">
        <h2 class="text-lg font-semibold text-n-slate-12 mb-2">
          Escolha a versão LIA
        </h2>
        <p class="text-sm text-n-slate-11 mb-6">
          Selecione qual versão do LIA você deseja usar para este agente.
        </p>

        <!-- No License Warning -->
        <div
          v-if="!isLoadingTemplates && !entitlements.has_access"
          class="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
        >
          <div class="flex items-center gap-3">
            <span class="i-lucide-alert-circle size-5 text-red-600" />
            <div>
              <p class="text-sm font-medium text-red-800 dark:text-red-200">
                Você não tem licença LIA ativa
              </p>
              <p class="text-xs text-red-700 dark:text-red-300">
                Faça upgrade do seu plano para criar agentes LIA
              </p>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="isLoadingTemplates" class="flex justify-center py-12">
          <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
        </div>

        <!-- Version Cards -->
        <div v-else class="space-y-4">
          <button
            v-for="version in availableVersions"
            :key="version.slug"
            class="w-full flex items-center gap-4 p-5 border-2 rounded-xl text-left transition-all"
            :class="[
              version.can_create
                ? 'hover:shadow-md cursor-pointer'
                : 'opacity-50 cursor-not-allowed',
              selectedVersion?.slug === version.slug
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                : 'border-n-weak hover:border-n-strong bg-n-solid-2',
            ]"
            :disabled="!version.can_create"
            @click="version.can_create && selectVersion(version)"
          >
            <div
              class="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              :class="
                selectedVersion?.slug === version.slug
                  ? 'bg-woot-100 dark:bg-woot-900/50'
                  : 'bg-n-alpha-2'
              "
            >
              <span
                :class="versionInfo[version.version]?.icon || 'i-lucide-bot'"
                class="size-7"
                :style="{
                  color:
                    selectedVersion?.slug === version.slug ? '#1f93ff' : '',
                }"
              />
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <h3 class="font-semibold text-n-slate-12">
                  {{ version.name }}
                </h3>
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-full"
                  :class="
                    version.can_create
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  "
                >
                  {{ version.remaining }}/{{ version.available }} disponível
                </span>
              </div>
              <p class="text-sm text-n-slate-11">
                {{
                  versionInfo[version.version]?.description || 'Agente de IA'
                }}
              </p>
            </div>
            <span
              v-if="version.can_create"
              class="i-lucide-chevron-right size-5 text-n-slate-11"
            />
          </button>
        </div>

        <!-- Limit Reached Warning -->
        <div
          v-if="
            !isLoadingTemplates &&
            entitlements.has_access &&
            !availableVersions.some(v => v.can_create)
          "
          class="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl"
        >
          <div class="flex items-center gap-3">
            <span class="i-lucide-alert-triangle size-5 text-yellow-600" />
            <div>
              <p
                class="text-sm font-medium text-yellow-800 dark:text-yellow-200"
              >
                Limite de agentes atingido
              </p>
              <p class="text-xs text-yellow-700 dark:text-yellow-300">
                Você já está usando todos os agentes LIA incluídos no seu plano.
                Contrate licenças adicionais para criar mais agentes.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Choose Template -->
      <div v-if="currentStep === 2" class="p-6">
        <div class="flex items-center gap-3 mb-2">
          <span
            :class="
              versionInfo[selectedVersion?.version]?.icon || 'i-lucide-bot'
            "
            class="size-5 text-woot-500"
          />
          <h2 class="text-lg font-semibold text-n-slate-12">
            Templates para {{ selectedVersion?.name }}
          </h2>
        </div>
        <p class="text-sm text-n-slate-11 mb-6">
          Escolha um modelo para começar. Você poderá personalizar tudo depois.
        </p>

        <!-- Templates Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            v-for="template in filteredTemplates"
            :key="template.id"
            class="flex flex-col items-start p-5 border-2 rounded-xl text-left transition-all hover:shadow-md"
            :class="
              selectedTemplate?.id === template.id
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                : 'border-n-weak hover:border-n-strong bg-n-solid-2'
            "
            @click="selectTemplate(template)"
          >
            <div
              class="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
              :class="
                selectedTemplate?.id === template.id
                  ? 'bg-woot-100 dark:bg-woot-900/50'
                  : 'bg-n-alpha-2'
              "
            >
              <span
                :class="getTemplateIcon(template)"
                class="size-6"
                :style="{
                  color: selectedTemplate?.id === template.id ? '#1f93ff' : '',
                }"
              />
            </div>
            <h3 class="font-semibold text-n-slate-12 mb-1">
              {{ template.name }}
            </h3>
            <p class="text-sm text-n-slate-11 line-clamp-2">
              {{ template.description }}
            </p>
          </button>
        </div>

        <!-- No Templates Message -->
        <div
          v-if="filteredTemplates.length === 0"
          class="text-center py-12 bg-n-alpha-1 rounded-xl border border-n-weak"
        >
          <span
            class="i-lucide-file-question size-12 text-n-slate-10 mx-auto mb-3"
          />
          <p class="text-sm text-n-slate-11">
            Nenhum template disponível para {{ selectedVersion?.name }}
          </p>
          <p class="text-xs text-n-slate-10 mt-2">
            Você pode continuar sem template e configurar manualmente.
          </p>
          <button
            type="button"
            class="mt-4 px-6 py-2.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90 transition-colors"
            @click="currentStep = 3"
          >
            Continuar sem template
          </button>
        </div>
      </div>

      <!-- Step 3: Configure Agent -->
      <div v-if="currentStep === 3" class="p-6 max-w-2xl mx-auto">
        <h2 class="text-lg font-semibold text-n-slate-12 mb-2">
          {{ t('LIA_AGENTS.CREATE.STEP2_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mb-6">
          {{ t('LIA_AGENTS.CREATE.STEP2_DESCRIPTION') }}
        </p>

        <div class="space-y-6">
          <!-- Name -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('LIA_AGENTS.CREATE.NAME_LABEL') }}
            </label>
            <input
              v-model="agentConfig.name"
              type="text"
              class="w-full px-4 py-2.5 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
              :placeholder="t('LIA_AGENTS.CREATE.NAME_PLACEHOLDER')"
            />
          </div>

          <!-- Company Name -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('LIA_AGENTS.CREATE.COMPANY_LABEL') }}
            </label>
            <input
              v-model="agentConfig.company_name"
              type="text"
              class="w-full px-4 py-2.5 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
              :placeholder="t('LIA_AGENTS.CREATE.COMPANY_PLACEHOLDER')"
            />
          </div>

          <!-- System Prompt -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-n-slate-12">
                {{ t('LIA_AGENTS.CREATE.PROMPT_LABEL') }}
              </label>
              <button
                type="button"
                class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-woot-500 to-purple-500 text-white rounded-lg hover:from-woot-600 hover:to-purple-600 transition-all"
                @click="showPromptAssistant = true"
              >
                <span class="i-lucide-sparkles size-3" />
                Gerar com IA
              </button>
            </div>
            <p class="text-xs text-n-slate-11 mb-2">
              {{ t('LIA_AGENTS.CREATE.PROMPT_HELP') }}
            </p>
            <textarea
              v-model="agentConfig.system_prompt"
              rows="12"
              class="w-full px-4 py-3 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent resize-y min-h-[200px] font-mono text-sm"
              :placeholder="t('LIA_AGENTS.CREATE.PROMPT_PLACEHOLDER')"
            />
            <p class="mt-1.5 text-xs text-n-slate-11">
              {{ agentConfig.system_prompt?.length || 0 }} caracteres
            </p>
          </div>

          <!-- Language -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('LIA_AGENTS.CREATE.LANGUAGE_LABEL') }}
            </label>
            <select
              v-model="agentConfig.language"
              class="w-full px-4 py-2.5 pr-10 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent appearance-none select-caret"
            >
              <option v-for="lang in languages" :key="lang.id" :value="lang.id">
                {{ lang.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Next Button -->
        <div class="flex justify-end mt-8">
          <button
            class="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors"
            :class="
              isStep3Valid
                ? 'bg-woot-500 hover:bg-woot-600'
                : 'bg-n-alpha-3 cursor-not-allowed'
            "
            :disabled="!isStep3Valid"
            @click="goNext"
          >
            {{ t('LIA_AGENTS.CREATE.NEXT') }}
            <span class="i-lucide-arrow-right size-4" />
          </button>
        </div>
      </div>

      <!-- Step 4: Select Inboxes -->
      <div v-if="currentStep === 4" class="p-6 max-w-2xl mx-auto">
        <h2 class="text-lg font-semibold text-n-slate-12 mb-2">
          {{ t('LIA_AGENTS.CREATE.STEP3_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mb-6">
          {{ t('LIA_AGENTS.CREATE.STEP3_DESCRIPTION') }}
        </p>

        <!-- Inboxes List -->
        <div class="space-y-3">
          <button
            v-for="inbox in inboxes"
            :key="inbox.id"
            class="w-full flex items-center gap-4 p-4 border-2 rounded-xl text-left transition-all"
            :class="
              agentConfig.inbox_ids.includes(inbox.id)
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                : 'border-n-weak hover:border-n-strong bg-n-solid-2'
            "
            @click="toggleInbox(inbox.id)"
          >
            <div
              class="w-10 h-10 rounded-lg flex items-center justify-center"
              :class="
                agentConfig.inbox_ids.includes(inbox.id)
                  ? 'bg-woot-100 dark:bg-woot-900/50'
                  : 'bg-n-alpha-2'
              "
            >
              <span class="i-lucide-inbox size-5 text-n-slate-11" />
            </div>
            <div class="flex-1">
              <p class="font-medium text-n-slate-12">{{ inbox.name }}</p>
              <p class="text-xs text-n-slate-11">{{ inbox.channel_type }}</p>
            </div>
            <div
              class="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
              :class="
                agentConfig.inbox_ids.includes(inbox.id)
                  ? 'border-woot-500 bg-woot-500'
                  : 'border-n-strong'
              "
            >
              <span
                v-if="agentConfig.inbox_ids.includes(inbox.id)"
                class="i-lucide-check size-3 text-white"
              />
            </div>
          </button>
        </div>

        <!-- Create Button -->
        <div class="flex justify-end mt-8">
          <button
            class="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors"
            :class="
              isStep4Valid && !isSaving
                ? 'bg-woot-500 hover:bg-woot-600'
                : 'bg-n-alpha-3 cursor-not-allowed'
            "
            :disabled="!isStep4Valid || isSaving"
            @click="createAgent"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-check size-4" />
            {{
              isSaving
                ? t('LIA_AGENTS.CREATE.CREATING')
                : t('LIA_AGENTS.CREATE.CREATE_BUTTON')
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Prompt AI Assistant Modal -->
    <Teleport to="body">
      <div
        v-if="showPromptAssistant"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-black/50"
          @click="showPromptAssistant = false"
        />
        <div
          class="relative bg-n-solid-1 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
        >
          <!-- Modal Header -->
          <div
            class="sticky top-0 bg-n-solid-1 px-6 py-4 border-b border-n-weak flex items-center justify-between"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-gradient-to-br from-woot-500 to-purple-500 flex items-center justify-center"
              >
                <span class="i-lucide-sparkles size-5 text-white" />
              </div>
              <div>
                <h3 class="text-lg font-semibold text-n-slate-12">
                  Assistente de Prompt
                </h3>
                <p class="text-sm text-n-slate-11">
                  Gere um prompt personalizado com IA
                </p>
              </div>
            </div>
            <button
              class="p-2 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors"
              @click="showPromptAssistant = false"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Modal Content -->
          <div class="p-6 space-y-6">
            <!-- Business Type -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                Tipo de negócio
              </label>
              <input
                v-model="promptAssistantInput.businessType"
                type="text"
                placeholder="Ex: telecomunicações, e-commerce, saúde, educação..."
                class="w-full px-4 py-2.5 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>

            <!-- Agent Role -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                Função do agente
              </label>
              <input
                v-model="promptAssistantInput.agentRole"
                type="text"
                placeholder="Ex: atendente de suporte, vendedor, secretária virtual..."
                class="w-full px-4 py-2.5 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>

            <!-- Tone -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-3">
                Tom de comunicação
              </label>
              <div class="grid grid-cols-2 gap-3">
                <button
                  v-for="tone in toneOptions"
                  :key="tone.id"
                  type="button"
                  class="flex flex-col items-start p-4 border-2 rounded-xl text-left transition-all"
                  :class="
                    promptAssistantInput.tone === tone.id
                      ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                      : 'border-n-weak hover:border-n-strong bg-n-solid-3'
                  "
                  @click="promptAssistantInput.tone = tone.id"
                >
                  <span class="font-medium text-n-slate-12">{{
                    tone.name
                  }}</span>
                  <span class="text-xs text-n-slate-11">{{ tone.desc }}</span>
                </button>
              </div>
            </div>

            <!-- Features -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-3">
                Funcionalidades
              </label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="feature in featureOptions"
                  :key="feature.id"
                  type="button"
                  class="px-3 py-2 text-sm border-2 rounded-lg transition-all"
                  :class="
                    promptAssistantInput.features.includes(feature.id)
                      ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400'
                      : 'border-n-weak hover:border-n-strong text-n-slate-11'
                  "
                  @click="toggleFeature(feature.id)"
                >
                  {{ feature.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div
            class="sticky bottom-0 bg-n-solid-1 px-6 py-4 border-t border-n-weak flex justify-end gap-3"
          >
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors"
              @click="showPromptAssistant = false"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="flex items-center gap-2 px-6 py-2 text-sm font-medium bg-gradient-to-r from-woot-500 to-purple-500 text-white rounded-lg hover:from-woot-600 hover:to-purple-600 transition-all disabled:opacity-50"
              :disabled="isGeneratingPrompt"
              @click="generatePromptWithAI"
            >
              <span
                v-if="isGeneratingPrompt"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span v-else class="i-lucide-sparkles size-4" />
              {{ isGeneratingPrompt ? 'Gerando...' : 'Gerar Prompt' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.select-caret {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none' stroke='rgb%28110,111,115%29' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M2 2.5 6 6 10 2.5'/></svg>");
  background-origin: content-box;
  background-position: right -1rem center;
  background-repeat: no-repeat;
  background-size: 10px 7px;
}
</style>
